import { useEffect, useRef, type ReactNode } from 'react';
import type { ConnState } from '../game/types';
import { Stamp } from './Basics';
import { Icon, type IconName } from './Icon';

/* ---------------- Pause overlay ---------------- */
export function PauseOverlay({ size = 'phone' }: { size?: 'phone' | 'tv' }) {
  const tv = size === 'tv';
  return (
    <div className="ex-overlay mo-fade" role="alertdialog" aria-label="Exam paused" style={{ padding: 24 }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tv ? 36 : 18, textAlign: 'center' }}>
        <Stamp variant="gold" size={tv ? 88 : 26} sub={tv ? 'by order of the host' : undefined}>{!tv && <Icon name="pause" size={30} />}{tv ? 'Exam suspended' : 'Suspended'}</Stamp>
        {!tv && <p className="ex-display" style={{ margin: '8px 0 0', fontSize: 32 }}>The host paused the exam.</p>}
        <p style={{ margin: tv ? '20px 0 0' : 0, fontSize: tv ? 40 : 18, color: '#B9C3E6' }}>The clock is frozen. {tv ? 'Answers already saved are safe.' : 'Your saved answer is safe.'}</p>
      </div>
    </div>
  );
}

/* ---------------- Connection notice ---------------- */
const CONN: Record<Exclude<ConnState, 'online'>, { phone: [string, IconName]; tv: [string, IconName] }> = {
  offline: { phone: ['No connection. Trying to reconnect…', 'wifiOff'], tv: ['Display lost contact with the exam office. Showing the last screen. Reconnecting…', 'wifiOff'] },
  reconnecting: { phone: ['Reconnecting to the exam office…', 'refresh'], tv: ['Reconnecting the display…', 'refresh'] },
  restored: { phone: ['Back online. You’re up to date.', 'wifi'], tv: ['Display back online. Showing the current screen.', 'wifi'] },
};
export function ConnectionNotice({ conn, scope = 'phone' }: { conn: ConnState; scope?: 'phone' | 'tv' }) {
  if (conn === 'online') return null;
  const [text, icon] = CONN[conn][scope];
  const tv = scope === 'tv';
  return (
    <div role="status" className={`ex-notice ex-notice--${conn} mo-slideup`}
      style={{ borderRadius: 0, justifyContent: 'center', padding: tv ? 20 : '12px 16px', fontSize: tv ? 30 : undefined, zIndex: 450, ...(tv ? { position: 'absolute', left: 0, right: 0, top: 0 } : null) }}>
      <Icon name={icon} size={tv ? 30 : 20} /><span>{text}</span>
    </div>
  );
}

/* ---------------- Toast ---------------- */
export function Toast({ children, size = 'phone' }: { children: ReactNode; size?: 'phone' | 'tv' }) {
  const tv = size === 'tv';
  return (
    <div role="status" className="ex-notice ex-notice--info mo-rise"
      style={tv ? { position: 'absolute', left: '50%', bottom: 140, transform: 'translateX(-50%)', fontSize: 30, padding: '22px 32px', zIndex: 600, boxShadow: '0 20px 50px rgba(0,0,0,.5)' }
        : { position: 'absolute', left: 12, right: 12, bottom: 76, zIndex: 600, boxShadow: '0 10px 30px rgba(0,0,0,.4)' }}>
      <Icon name="info" size={tv ? 30 : 20} /><span>{children}</span>
    </div>
  );
}

/* ---------------- Confirmation dialog ---------------- */
export function ConfirmDialog({ title, body, confirmLabel, cancelLabel = 'Keep playing', onConfirm, onCancel }:
  { title: string; body: string; confirmLabel: string; cancelLabel?: string; onConfirm: () => void; onCancel: () => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    cancelRef.current?.focus();
    return () => { if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus(); };
  }, []);
  return (
    <div className="ex-scrim mo-fade" onKeyDown={(e) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      if (e.key !== 'Tab') return;
      const buttons = dialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
      if (!buttons?.length) return;
      const first = buttons[0], last = buttons[buttons.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }}>
      <section ref={dialogRef} className="ex-dialog" role="alertdialog" aria-modal="true" aria-labelledby="dlg-h" aria-describedby="dlg-p">
        <h2 id="dlg-h" style={{ margin: '0 0 10px', font: '700 24px/1.2 var(--font-ui)' }}>{title}</h2>
        <p id="dlg-p" style={{ margin: '0 0 24px', fontSize: 16, lineHeight: 1.5, color: '#4A4F66' }}>{body}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button ref={cancelRef} type="button" className="ex-btn ex-btn--quiet" onClick={onCancel}>{cancelLabel}</button>
          <button type="button" className="ex-btn ex-btn--danger-solid" onClick={onConfirm}>{confirmLabel}</button>
        </div>
      </section>
    </div>
  );
}

/* ---------------- Host control bar ---------------- */
/** Routine controls on the left, the disruptive action separated on the right. */
export function HostControlBar({ paused, hasQuestion, disabled = false, onPause, onResume, onSkip, onEnd }:
  { paused: boolean; hasQuestion: boolean; disabled?: boolean; onPause: () => void; onResume: () => void; onSkip: () => void; onEnd: () => void }) {
  return (
    <footer aria-label="Game controls" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 12, padding: '14px 28px 18px', borderTop: '1px solid rgba(14,20,48,.14)', background: '#FBF7EE', flexWrap: 'wrap' }}>
      {paused
        ? <button type="button" className="ex-btn ex-btn--primary ex-btn--lg" disabled={disabled} style={{ minWidth: 200 }} onClick={onResume}><Icon name="play" />Resume</button>
        : <button type="button" className="ex-btn ex-btn--paper ex-btn--lg" disabled={disabled} style={{ minWidth: 200 }} onClick={onPause}><Icon name="pause" />Pause</button>}
      <button type="button" className="ex-btn ex-btn--quiet ex-btn--lg" disabled={disabled} onClick={onSkip}><Icon name="skip" />{hasQuestion ? 'Skip question' : 'Skip to next'}</button>
      <span style={{ flex: 1 }} />
      <button type="button" className="ex-btn ex-btn--danger" disabled={disabled} onClick={onEnd}><Icon name="flag" />End game early</button>
    </footer>
  );
}
